import {Html5Qrcode} from 'html5-qrcode';
import {useEffect, useRef} from 'react';

type QRScannerProps = {
  onScanSuccess: (decodedText: string) => void;
  onScanFailure: (errorMessage: string) => void;
  onClose: () => void;
};

const getScannerStartErrorMessage = (error: unknown): string => {
  const message = (
    error instanceof Error ? error.message : String(error ?? '')
  ).toLowerCase();

  if (message.includes('permission') || message.includes('notallowed')) {
    return 'دسترسی به دوربین داده نشده است. لطفاً دسترسی دوربین را فعال کنید.';
  }

  if (
    message.includes('notfound') ||
    message.includes('no camera') ||
    message.includes('camera')
  ) {
    return 'امکان دسترسی به دوربین وجود ندارد. لطفاً دوربین خود را بررسی کنید.';
  }

  return 'امکان راه‌اندازی دوربین وجود ندارد. لطفاً دوباره تلاش کنید.';
};

export default function QRScanner({
  onScanSuccess,
  onScanFailure,
  onClose,
}: QRScannerProps) {
  // Keep the latest callbacks in refs so the effect below runs only once.
  // Parent passes inline functions -> new identity on every render.
  const onScanSuccessRef = useRef(onScanSuccess);
  const onScanFailureRef = useRef(onScanFailure);

  useEffect(() => {
    onScanSuccessRef.current = onScanSuccess;
    onScanFailureRef.current = onScanFailure;
  });

  useEffect(() => {
    let cancelled = false;
    let handled = false;

    const scanner = new Html5Qrcode('qr-reader');

    const handleScanSuccess = (decodedText: string) => {
      if (handled || cancelled) {
        return;
      }
      handled = true;

      // Scanner is stopped by the unmount cleanup once the parent closes it,
      // so just notify the parent.
      onScanSuccessRef.current(decodedText);
    };

    // Called for EVERY frame (10x/sec) in which no QR is found.
    // It's not a real error, so do nothing here.
    const handleFrameWithoutQr = () => {};

    const startPromise = scanner
      .start(
        {facingMode: 'environment'},
        {
          fps: 10,
          qrbox: {width: 250, height: 250},
          aspectRatio: 1.0,
        },
        handleScanSuccess,
        handleFrameWithoutQr,
      )
      .catch(error => {
        if (cancelled) {
          return;
        }
        console.error('Failed to start scanner:', error);
        onScanFailureRef.current(getScannerStartErrorMessage(error));
      });

    return () => {
      cancelled = true;

      // Wait for start() to settle before stopping, otherwise stop() can
      // throw while the camera is still initializing (and StrictMode
      // double-mount leaves a dangling camera stream).
      startPromise
        .then(() => {
          if (scanner.isScanning) {
            return scanner.stop();
          }
        })
        .then(() => scanner.clear())
        .catch(error => {
          console.error(error);
        });
    };
  }, []);

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black/70">
      <div className="p-4 w-full max-w-md bg-white rounded-lg">
        <div className="mb-2 text-sm text-center text-gray-600">
          کیو آر کد را در کادر قرار دهید
        </div>

        <div id="qr-reader" className="w-full" />

        <button
          type="button"
          className="p-2 mt-4 w-full text-white bg-red-500 rounded"
          onClick={onClose}
        >
          بستن
        </button>
      </div>
    </div>
  );
}
